import React, { useState, useEffect } from "react";
import { currentDateFormat } from "../utils";
import axios from "axios";
import Swal from "../messageBox/customSweetAlert";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../redux/loaderAction";
import { GET_UPLOAD_SCHEDULE_HISTORY, DOWNLOAD_SCHEDULE_FILE } from "../constant";
import moment from "moment";
import Pagination from "react-responsive-pagination";
import Table from "react-bootstrap/Table";
import "bootstrap/dist/css/bootstrap.min.css";

function ScheduleUploadHistory() {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.loader.loading);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [uploadHistory, setUploadHistory] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit] = useState(10);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchUploadHistory();
  }, [currentPage]);

  const fetchUploadHistory = async () => {
    setIsLoading(true);
    dispatch(setLoading(true));
    try {
      const token = localStorage.getItem("token") && localStorage.getItem("token") !== "undefined"
        ? JSON.parse(localStorage.getItem("token"))
        : "";
      const response = await axios.get(GET_UPLOAD_SCHEDULE_HISTORY, {
        params: {
          page: currentPage,
          limit: limit,
        },
        headers: {
          authorization: token,
        },
      });

      if (response?.data?.status === "200") {
        setUploadHistory(response?.data?.data || []);
        setTotalPages(response?.data?.pagination?.totalPages || 1);
        setTotalRecords(response?.data?.pagination?.totalRecords || 0);
      } else {
        setUploadHistory([]);
        Swal.fire({
          title: "Error!",
          text: response?.data?.message || "Failed to fetch upload history",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching upload history:", error);
      setUploadHistory([]);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to fetch upload history",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
      dispatch(setLoading(false));
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return moment(dateString).format("MM/DD/YYYY hh:mm A");
  };

  const handleDownload = async (filePath, fileName) => {
    if (!filePath) {
      Swal.fire({
        title: "Error!",
        text: "File path not available",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    dispatch(setLoading(true));
    try {
      const token = localStorage.getItem("token") && localStorage.getItem("token") !== "undefined"
        ? JSON.parse(localStorage.getItem("token"))
        : "";

      // Handle file path - replace backslashes with forward slashes and encode
      const normalizedPath = filePath.replace(/\\/g, '/');
      const encodedFilePath = encodeURIComponent(normalizedPath);
      const downloadUrl = `${DOWNLOAD_SCHEDULE_FILE}/${encodedFilePath}`;

      const response = await axios.get(downloadUrl, {
        headers: {
          authorization: token,
          "Content-Type": "text/plain",
        },
        responseType: "blob",
      });

      // Create a blob from the response
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "schedule_file.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        title: "Success!",
        text: "File downloaded successfully",
        icon: "success",
        confirmButtonText: "OK",
      });
    } catch (error) {
      console.error("Error downloading file:", error);
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || "Failed to download file",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div>
      <section style={{ borderBottom: "solid 1px #eee", marginTop: "0px" }}>
        <div className="d-flex justify-content-between form-container">
          <div>
            <h2 className="fs-5 font-weight-600">Schedule Upload History</h2>
          </div>
          <div>
            <span>{currentDateFormat(currentDate)}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="create-schedule-table" style={{ padding: "10px" }}>
          <div className="table-responsive">
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                <p style={{ fontSize: "16px" }}>Loading...</p>
              </div>
            ) : uploadHistory.length > 0 ? (
              <>
                <Table striped bordered hover className="tbl-require">
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center" }}>#</th>
                      <th style={{ width: "780px", maxWidth: "780px" }}>File Name</th>
                      <th style={{ textAlign: "center" }}>Payor</th>
                      <th style={{ textAlign: "center" }}>Total Records</th>
                      <th style={{ textAlign: "center" }}>New</th>
                      <th style={{ textAlign: "center" }}>Processed</th>
                      <th style={{ textAlign: "center" }}>Skipped</th>
                      <th style={{ textAlign: "center" }}>Failed</th>
                      <th style={{ textAlign: "center" }}>Upload Date & Time</th>
                      <th style={{ textAlign: "center" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadHistory.map((item, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "center" }}>{(currentPage - 1) * limit + index + 1}</td>
                        <td 
                          style={{ 
                            // textAlign: "center", 
                            width: "200px", 
                            maxWidth: "200px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            wordBreak: "break-all"
                          }}
                          title={item.file_name || "N/A"}
                        >
                          {item.file_name || "N/A"}
                        </td>
                        <td style={{ textAlign: "center" }}>{item.payer?.payer_name || "N/A"}</td>
                        <td style={{ textAlign: "center" }}>{item.no_of_records || 0}</td>
                        <td style={{ textAlign: "center", color: "#34A87E", fontWeight: "500" }}>
                          {item.no_of_new_records || 0}
                        </td>
                        <td style={{ textAlign: "center", color: "#34A87E", fontWeight: "500" }}>
                          {item.no_of_successful_records || 0}
                        </td>
                        <td style={{ textAlign: "center", color: "#DC2626", fontWeight: "500" }}>
                          {item.no_of_records_skipped || 0}
                        </td>
                        <td style={{ textAlign: "center", color: "#DC2626", fontWeight: "500" }}>
                          {item.no_of_failed_records || 0}
                        </td>
                        <td style={{ textAlign: "center" }}>{formatDate(item.file_created_at)}</td>
                        <td style={{ textAlign: "center", cursor: "pointer" }}>
                          <i
                            className="pi pi-download"
                            title="Download"
                            style={{ fontSize: "18px", color: "#31876f" }}
                            onClick={() => handleDownload(item.file_path, item.file_name)}
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}>
                    <Pagination
                      current={currentPage}
                      total={totalPages}
                      onPageChange={(page) => setCurrentPage(page)}
                      marginPagesDisplayed={2}
                      pageRangeDisplayed={5}
                      containerClassName={"pagination"}
                      subContainerClassName={"pages pagination"}
                      activeClassName={"active"}
                    />
                  </div>
                )}

                <div style={{ marginTop: "15px", textAlign: "center", color: "#6b7280", fontSize: "14px" }}>
                  Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, totalRecords)} of {totalRecords} records
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>
                <p style={{ fontSize: "16px" }}>No upload history found</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default ScheduleUploadHistory;

